// ─── Click-to-book: the pure half ──────────────────────────────────────────────
//
// Everything here is PURE (no imports beyond types, no DOM, no Date-parsing of
// naive strings) so it can be unit-tested directly. The calendar views own the
// pixels; this module owns the arithmetic and the narrowing rules.
//
// TIME MODEL: identical to lib/calendar.ts — a calendar event's `start` is a
// tenant-local naive wall-clock string ('YYYY-MM-DDTHH:mm[:ss]'). Times are
// carried around as MINUTES FROM MIDNIGHT of the anchor date, which is the same
// unit the day/resource grids already position with. Nothing here constructs a
// `Date`: `new Date('2026-08-10T14:30')` is parsed in the BROWSER's timezone,
// which silently shifts an operator in a different timezone from their tenant.

import type { CalendarEvent } from './render'

/** A service as the booking dialog needs it — the subset of ServiceRecord it reads. */
export interface BookableService {
  id: string
  name: string
  duration_minutes: number
  price: number
  currency: string
  resource_ids: string[]
  category: string | null
  display_order: number
}

/** One clickable band of empty time in one machine column. */
export interface SlotBand {
  /** Minutes from midnight of the anchor date. */
  startMinutes: number
  endMinutes: number
  /** 'HH:mm' — the label and the value sent to the API. */
  time: string
}

/** What a click on empty calendar space resolved to. */
export interface SlotPick {
  /** Anchor date, 'YYYY-MM-DD'. */
  date: string
  /** Proposed start, minutes from midnight, already snapped to the slot grid. */
  startMinutes: number
  /**
   * The machine whose column was clicked, or null for the "Unassigned" column.
   * This NARROWS THE SERVICE LIST and nothing more — it is not the machine the
   * booking lands on. Resources are auto-selected server-side (a Critical Rule:
   * POST /v1-bookings takes no resource_ids), so the UI must never promise the
   * clicked machine.
   */
  resourceId: string | null
  resourceName: string | null
}

// ─── Time arithmetic ───────────────────────────────────────────────────────────

/**
 * Minutes from midnight for a tenant-local naive datetime string. Reads the
 * characters rather than parsing a Date, so the browser's timezone can never
 * shift it.
 */
export function minutesFromLocalTime(iso: string): number {
  const hour = Number(iso.slice(11, 13))
  const minute = Number(iso.slice(14, 16))
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0
  return hour * 60 + minute
}

/** 'HH:mm' for minutes from midnight. Wraps nothing — callers stay within a day. */
export function minutesToTimeString(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** '2:30 PM' — a 12-hour label for a band or a slot. */
export function minutesToLabel(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/**
 * The naive tenant-local datetime the API wants: 'YYYY-MM-DDTHH:mm'. This is the
 * inbound half of the platform's timezone convention — a naive string IS tenant
 * local, so no offset and no conversion.
 */
export function localDateTime(date: string, totalMinutes: number): string {
  return `${date}T${minutesToTimeString(totalMinutes)}`
}

/** Floor to the slot grid, so clicking anywhere inside the 2:30 band means 2:30. */
export function snapToInterval(totalMinutes: number, intervalMinutes: number): number {
  if (intervalMinutes <= 0) return totalMinutes
  return Math.floor(totalMinutes / intervalMinutes) * intervalMinutes
}

// ─── Bands: the clickable empty time in one column ─────────────────────────────

export interface BusyRange {
  startMinutes: number
  endMinutes: number
}

/**
 * The bookings in one machine column, as busy ranges. Only statuses that really
 * hold the machine count: a cancelled or superseded booking has released its
 * slot, which is exactly why those two are excluded from every utilisation
 * rollup in the platform.
 */
export function busyRangesFor(events: CalendarEvent[]): BusyRange[] {
  return events
    .filter(e => e.status !== 'cancelled' && e.status !== 'rescheduled')
    .map(e => ({
      startMinutes: minutesFromLocalTime(e.start),
      endMinutes: minutesFromLocalTime(e.end),
    }))
    .sort((a, b) => a.startMinutes - b.startMinutes)
}

/**
 * Every bookable-looking band of empty time in one column, stepped by the
 * tenant's slot interval across the drawn day window.
 *
 * A band is a PROPOSAL, not a promise. This function knows about bookings and
 * about closures the operator can SEE on the grid — business hours, resource
 * schedules and buffers all live in `GET /v1-availability`, which the dialog
 * consults once a service is chosen. Offering a band the API then refuses is
 * fine and expected; the dialog answers with the real open slots.
 *
 * What must NOT happen is offering a band on top of something already DRAWN in
 * that column. A booking is filtered by `busy`; a closure by `blocked`. Both are
 * visible to the operator, so a "+ 2:30" invitation sitting on top of one reads
 * as the app contradicting itself — and in the closure's case it is an
 * invitation that could only ever fail, since availability excludes the window
 * for every service.
 */
export function buildSlotBands(input: {
  dayStartMinutes: number
  dayEndMinutes: number
  intervalMinutes: number
  busy: BusyRange[]
  /**
   * Closures overlapping this column: a block on this machine, or a
   * business-wide block, already clipped to the anchor day by the caller.
   * Optional so an existing caller keeps compiling — absent means none known,
   * which is the pre-existing behaviour.
   */
  blocked?: BusyRange[]
}): SlotBand[] {
  const { dayStartMinutes, dayEndMinutes, busy } = input
  const blocked = input.blocked ?? []
  const interval = input.intervalMinutes > 0 ? input.intervalMinutes : 30
  const bands: SlotBand[] = []

  // Start from the grid boundary at or after the top of the drawn window, so
  // bands line up with the half-hour rules the operator can see.
  let cursor = snapToInterval(dayStartMinutes, interval)
  if (cursor < dayStartMinutes) cursor += interval

  for (; cursor + interval <= dayEndMinutes; cursor += interval) {
    const end = cursor + interval
    const occupied = busy.some(range => range.startMinutes < end && range.endMinutes > cursor)
    if (occupied) continue
    const closed = blocked.some(range => range.startMinutes < end && range.endMinutes > cursor)
    if (closed) continue
    bands.push({ startMinutes: cursor, endMinutes: end, time: minutesToTimeString(cursor) })
  }

  return bands
}

// ─── Closures, as the grid needs them ──────────────────────────────────────────

/** A closure as `GET /v1-availability-config/time-blocks` returns it. */
export interface TimeBlock {
  id: string
  resource_id: string | null
  /** Tenant-local naive 'YYYY-MM-DDTHH:mm[:ss]'. */
  block_start: string
  block_end: string
  reason: string | null
}

/**
 * The closures that apply to ONE column, clipped to the anchor day.
 *
 * Two things a caller must not get wrong, both handled here:
 *
 * 1. **A block with no `resource_id` closes the whole centre**, so it applies to
 *    every column including the synthetic "Unassigned" one. Reading a null as
 *    "belongs to no column" would draw a business-wide holiday nowhere and leave
 *    every machine looking open.
 * 2. **A block can span days** — "closed for Christmas" is one row covering a
 *    week. Clipping to the day window is what stops a multi-day closure being
 *    read as `startMinutes` far in the negative and silently missing the
 *    overlap test.
 *
 * Times are read character-wise from the naive strings for the same reason
 * everything else here is: `new Date('2026-08-10T14:30')` parses in the
 * BROWSER's timezone.
 */
export function blocksForColumn(
  blocks: TimeBlock[],
  resourceId: string | null,
  date: string,
): BusyRange[] {
  const ranges: BusyRange[] = []
  for (const block of blocks) {
    // Business-wide (null) applies everywhere; otherwise only to its own column.
    if (block.resource_id !== null && block.resource_id !== resourceId) continue

    const startsBefore = block.block_start.slice(0, 10) < date
    const endsAfter = block.block_end.slice(0, 10) > date
    // Entirely outside the anchor day.
    if (block.block_end.slice(0, 10) < date || block.block_start.slice(0, 10) > date) continue

    const startMinutes = startsBefore ? 0 : minutesFromLocalTime(block.block_start)
    const endMinutes = endsAfter ? 24 * 60 : minutesFromLocalTime(block.block_end)
    if (endMinutes <= startMinutes) continue
    ranges.push({ startMinutes, endMinutes })
  }
  return ranges.sort((a, b) => a.startMinutes - b.startMinutes)
}

// ─── Which services can the clicked column actually host ───────────────────────

/**
 * Services reachable from the clicked machine column.
 *
 * An EMPTY `resource_ids` means the service needs NO machine — `v1-bookings`
 * reads it as `resourcesRequired: 0`. It does NOT mean "runs on every machine".
 * That is the OPPOSITE of `packages.service_ids`, where empty means "covers
 * everything", and conflating the two is a bug this codebase has already paid
 * for once. So a no-machine service belongs to the Unassigned column only.
 */
export function servicesForResource(
  services: BookableService[],
  resourceId: string | null,
): BookableService[] {
  const matches = resourceId === null || resourceId === UNASSIGNED_COLUMN_ID
    ? services.filter(s => s.resource_ids.length === 0)
    : services.filter(s => s.resource_ids.includes(resourceId))
  return sortServices(matches)
}

/** The synthetic column id both day views use for events with no resource. */
export const UNASSIGNED_COLUMN_ID = 'unassigned'

export function sortServices(services: BookableService[]): BookableService[] {
  return [...services].sort(
    (a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name),
  )
}

/**
 * What a DRAG of an existing appointment resolved to.
 *
 * The counterpart of `SlotPick` (a click on empty time), and it carries the same
 * caveat about columns: `startMinutes` is a proposal about TIME only. Which
 * machine the appointment ends up on is re-picked server-side by `/reschedule`,
 * so nothing here names one.
 */
export interface MoveProposal {
  event: CalendarEvent
  /** Anchor date, 'YYYY-MM-DD'. A drag never crosses days — the grid is one. */
  date: string
  /** Proposed start, minutes from midnight, snapped to the slot grid. */
  startMinutes: number
}

// ─── Dragging an appointment to a new time ─────────────────────────────────────
//
// The arithmetic only. The grid owns the pixels and the pointer events; this owns
// what a vertical displacement MEANS, so it can be tested without a browser.

/** What a drag resolved to, once the pointer is released. */
export interface DragTarget {
  /** Proposed start, minutes from midnight, snapped to the slot grid. */
  startMinutes: number
  /** True once the proposal differs from where the appointment already sits. */
  moved: boolean
}

/**
 * Where a dragged tile wants to land.
 *
 * VERTICAL ONLY, and that is a contract rather than a simplification: resources
 * are auto-selected server-side (a Critical Rule — `/reschedule` re-picks the
 * machine and accepts no `resource_ids`), so a horizontal drag could not honour
 * the column it was dropped on. Letting a tile cross lanes would promise a
 * machine the API is free to ignore, which is the same lie the empty-slot menu
 * takes care not to tell.
 *
 * CLAMPED to the drawn window, both ends. A tile dragged off the top would
 * otherwise propose a negative time, and one dragged off the bottom a start whose
 * session runs past the last row on screen — both are proposals the operator
 * cannot see the consequence of. `durationMinutes` is what keeps the whole
 * appointment inside the window rather than just its first minute.
 */
export function dragTarget(input: {
  /** Where the appointment currently starts, minutes from midnight. */
  originalStartMinutes: number
  durationMinutes: number
  /** Pointer displacement since the drag began, in pixels. Down is positive. */
  deltaPixels: number
  pixelsPerMinute: number
  intervalMinutes: number
  dayStartMinutes: number
  dayEndMinutes: number
}): DragTarget {
  const {
    originalStartMinutes, durationMinutes, deltaPixels,
    pixelsPerMinute, intervalMinutes, dayStartMinutes, dayEndMinutes,
  } = input

  const interval = intervalMinutes > 0 ? intervalMinutes : 30
  const perMinute = pixelsPerMinute > 0 ? pixelsPerMinute : 1

  // Round rather than floor: the tile follows the pointer, so the nearest grid
  // line is the one the operator is aiming at. Flooring biases every drag
  // upward by up to a full interval, which reads as the tile lagging behind.
  const rawMinutes = originalStartMinutes + deltaPixels / perMinute
  const snapped = Math.round(rawMinutes / interval) * interval

  // The latest start that still leaves the whole session inside the window. A
  // session longer than the drawn day cannot be clamped into it, so the floor
  // wins — never propose a start before opening.
  const latestStart = Math.max(dayStartMinutes, dayEndMinutes - durationMinutes)
  const clamped = Math.min(Math.max(snapped, dayStartMinutes), latestStart)

  return { startMinutes: clamped, moved: clamped !== originalStartMinutes }
}

/**
 * Is this appointment allowed to be dragged at all?
 *
 * The same three conditions `PATCH /:id/reschedule` enforces, asked before the
 * gesture rather than after it: only a `booked` appointment can move, and not one
 * whose customer has already arrived. A tile that cannot move must not follow the
 * pointer — a drag that always snaps back teaches the operator to distrust the
 * grid, and it is far cheaper to refuse the gesture than to explain the 409.
 */
export function canDragBooking(event: {
  status: string
  checked_in_at?: string | null
  checked_out_at?: string | null
}): boolean {
  if (event.status !== 'booked') return false
  if (event.checked_in_at) return false
  if (event.checked_out_at) return false
  return true
}

/**
 * How far the pointer must travel before a press becomes a drag.
 *
 * Without a threshold every tap is a one-pixel drag and the booking sheet stops
 * opening. 5px is below the noise floor of a deliberate click and well under a
 * finger's wobble.
 */
export const DRAG_THRESHOLD_PX = 5

// ─── Reconciling the clicked time with real availability ───────────────────────

/** An open slot as `GET /v1-availability` returns it (tenant-local naive). */
export interface OpenSlot {
  start: string
  end: string
}

/** Is the proposed time actually one of the open slots for this service? */
export function findExactSlot(slots: OpenSlot[], startMinutes: number): OpenSlot | null {
  return slots.find(slot => minutesFromLocalTime(slot.start) === startMinutes) ?? null
}

/**
 * The open slots closest to the time that was clicked, nearest first, so a click
 * that landed outside business hours or on a blocked machine still answers with
 * something bookable rather than just "no".
 *
 * Ties break EARLIER: asked to fill a gap, an operator means the sooner one.
 */
export function nearestSlots(slots: OpenSlot[], startMinutes: number, count = 6): OpenSlot[] {
  return [...slots]
    .sort((a, b) => {
      const da = Math.abs(minutesFromLocalTime(a.start) - startMinutes)
      const db = Math.abs(minutesFromLocalTime(b.start) - startMinutes)
      if (da !== db) return da - db
      return minutesFromLocalTime(a.start) - minutesFromLocalTime(b.start)
    })
    .slice(0, count)
}
